// UsageOverview.tsx — Console Phase A Overview, bound to REAL data.
//
// Reads GET /api/platform/usage/spend and /usage/latency. Both are sub-routes
// inside platformAdmin.js and therefore inherit hostGate + operatorOnly
// structurally; this component adds no auth of its own and must not imply any.
//
// SCOPED TO `PT`. No import from theme.ts.
//
// EMPTY IS NOT ZERO. Every formatter renders null as an em dash rather than 0,
// and the empty state says explicitly whether the rollup has run. An operator
// looking at "$0.00" cannot tell "nothing was spent" from "the rollup never
// ran", and those need different responses.

import React, { useCallback, useEffect, useState } from "react";
import { apiCallEx } from "../../apiClient";
import { PT, PT_NUM, fmtCost, fmtNum, fmtMs, fmtTokens, rateColor } from "../../platformTheme";
import ModelPrices from "./ModelPrices";
import ProviderBilling from "./ProviderBilling";
import AiRequests from "./AiRequests";

interface SpendRow {
  tenant_id: string | null;
  tenant_name?: string | null;
  provider: string;
  provider_display_name?: string;
  provider_known?: boolean;
  model: string;
  calls: string;
  errors: string;
  input_tokens: string;
  output_tokens: string;
  est_cost: string;
  currency: string;
}
interface LatencyRow {
  tenant_id?: string | null;
  tenant_name?: string | null;
  provider: string;
  provider_display_name?: string;
  provider_known?: boolean;
  model: string;
  calls: string;
  errors: string;
  rerouted: string;
  skipped: string;
  p50_ms: number | null;
  p95_ms: number | null;
  max_ms: number | null;
}
interface ProviderSpend {
  provider: string;
  provider_display_name: string;
  is_active: boolean | null;
  in_catalog: boolean;
  calls: number; errors: number;
  input_tokens: number; output_tokens: number;
  est_cost: number; models: number;
}
interface ExcludedPair { provider: string; model: string; calls: number }
interface SilentProvider {
  provider: string;
  provider_display_name: string;
  is_active?: boolean;
}
interface FailedTenant { tenant_id: string | null; tenant_name: string; error: string }

/**
 * A customer's name, or an honest label when there isn't one.
 *
 * The old column rendered `tenant_id.slice(0, 8)` — a UUID stub. It is not that
 * an eight-character hex string is ugly; it is that an operator cannot tell one
 * customer from another with it, which is the entire job of this table.
 */
const tenantLabel = (r: { tenant_id?: string | null; tenant_name?: string | null }): string => {
  if (r.tenant_name) return r.tenant_name;
  if (!r.tenant_id) return "Platform / unattributed";
  return r.tenant_id.slice(0, 8);
};

/** Catalog display name where we have one; the stored slug where we do not. */
const providerLabel = (r: { provider: string; provider_display_name?: string }): string =>
  r.provider_display_name || r.provider;

const card: React.CSSProperties = {
  background: PT.bg2,
  border: `1px solid ${PT.brd}`,
  borderRadius: PT.radiusLg,
  padding: 16,
};

const th: React.CSSProperties = {
  textAlign: "left",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.5,
  textTransform: "uppercase",
  color: PT.txD,
  padding: "8px 10px",
  borderBottom: `1px solid ${PT.brd}`,
  whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  fontSize: 12.5,
  padding: "9px 10px",
  borderBottom: `1px solid ${PT.brdSubtle}`,
  whiteSpace: "nowrap",
};

const Stat: React.FC<{ label: string; value: string; sub?: string; color?: string }> = ({
  label, value, sub, color,
}) => (
  <div style={{ ...card, flex: 1, minWidth: 150 }}>
    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: PT.txD }}>
      {label}
    </div>
    <div style={{ ...PT_NUM, fontSize: 24, fontWeight: 600, marginTop: 6, color: color || PT.tx }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: PT.txM, marginTop: 3 }}>{sub}</div>}
  </div>
);

export const UsageOverview: React.FC<{ days: number; focus?: "spend" | "latency" }> = ({ days, focus }) => {
  const [spend, setSpend] = useState<SpendRow[] | null>(null);
  const [latency, setLatency] = useState<LatencyRow[] | null>(null);
  const [totals, setTotals] = useState<{ calls: number; errors: number; est_cost: number; input_tokens?: number; output_tokens?: number } | null>(null);
  const [byProvider, setByProvider] = useState<ProviderSpend[]>([]);
  const [spendSource, setSpendSource] = useState<"rollup" | "live_events">("rollup");
  const [rollupCalls, setRollupCalls] = useState(0);
  const [excluded, setExcluded] = useState<ExcludedPair[]>([]);
  const [excludedCalls, setExcludedCalls] = useState(0);
  const [silent, setSilent] = useState<SilentProvider[]>([]);
  const [failedTenants, setFailedTenants] = useState<FailedTenant[]>([]);
  const [unattributedReadable, setUnattributedReadable] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [at, setAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    const [s, l] = await Promise.all([
      apiCallEx(`/platform/usage/spend?days=${days}`),
      apiCallEx(`/platform/usage/latency?days=${days}`),
    ]);
    // Surface a failure rather than rendering an empty table that looks like
    // "no usage" — the two are indistinguishable to the reader otherwise.
    if (!s.ok || !l.ok) {
      setErr(`Failed to load usage (spend ${s.status}, latency ${l.status})`);
      setSpend([]); setLatency([]); setTotals(null);
    } else {
      setSpend(s.data?.rows || []);
      setTotals(s.data?.totals || null);
      setLatency(l.data?.rows || []);
      // Providers with no traffic come from the routing catalog, so a provider
      // that has never been called is stated rather than silently missing.
      setByProvider(s.data?.by_provider || []);
      setSpendSource(s.data?.spend_source === "live_events" ? "live_events" : "rollup");
      setRollupCalls(Number(s.data?.rollup_calls || 0));
      // Rows the platform cannot account for are surfaced, never silently
      // dropped — see the panel at the foot of this screen.
      setExcluded([...(s.data?.excluded_unrecognised || []), ...(l.data?.excluded_unrecognised || [])]);
      setExcludedCalls(Number(s.data?.excluded_calls || 0) + Number(l.data?.excluded_calls || 0));
      setSilent(s.data?.providers_without_usage || l.data?.providers_without_usage || []);
      // A tenant whose slice failed to read makes the totals PARTIAL. Saying so
      // is the difference between a small number and a wrong one.
      setFailedTenants([...(s.data?.tenants_failed || []), ...(l.data?.tenants_failed || [])]);
      setUnattributedReadable(s.data?.unattributed_readable !== false);
    }
    setAt(new Date());
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading && spend === null) return <div style={{ color: PT.txM, fontSize: 13 }}>Loading usage…</div>;

  if (err) {
    return (
      <div style={{ ...card, borderColor: PT.err, background: PT.errS }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: PT.err }}>{err}</div>
        <button
          onClick={load}
          style={{
            marginTop: 10, fontFamily: PT.fontUI, fontSize: 12, padding: "5px 10px",
            borderRadius: PT.radius, border: `1px solid ${PT.brd}`, background: PT.bg2,
            color: PT.tx, cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const noSpend = !spend || spend.length === 0;
  const noLatency = !latency || latency.length === 0;

  // The distinction that matters: latency reads RAW events, spend reads the
  // ROLLUP. Events present with no rollup rows means the rollup has not run —
  // a different problem from no usage, and the operator is told which.
  const rollupLikelyNotRun = noSpend && !noLatency;

  const errRate = totals && totals.calls ? totals.errors / totals.calls : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* REFRESH. This screen had no refresh control at all — the only way to
          re-read was to change the window or leave and come back. The button
          reports its own state ("Refreshing…" + a timestamp that moves), because
          a refresh over unchanged data and a dead button look identical
          otherwise, and that is what "the refresh button does nothing" was. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 11.5, color: PT.txD }}>
          {at ? `updated ${at.toLocaleTimeString()}` : "not loaded yet"}
        </div>
        <div style={{ flex: 1 }} />
        <button
          onClick={load}
          disabled={loading}
          style={{
            fontFamily: PT.fontUI, fontSize: 11.5, padding: "4px 9px",
            borderRadius: PT.radius, border: `1px solid ${PT.brd}`,
            background: PT.bg2, color: PT.tx,
            cursor: loading ? "wait" : "pointer", opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {failedTenants.length > 0 && (
        <div style={{ ...card, borderColor: PT.err, background: PT.errS }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: PT.err }}>
            {failedTenants.length} customer{failedTenants.length > 1 ? "s" : ""} could not be read — the totals below are partial
          </div>
          <div style={{ fontSize: 12, color: PT.txM, marginTop: 4 }}>
            {failedTenants.map((f) => f.tenant_name).join(", ")}
          </div>
        </div>
      )}

      {/* The nightly summary being behind is NOT the same as no spend, and the
          screen must not let the two look alike. When the live figures are ahead
          the console shows those and says so, rather than reporting a stale
          number as though it were current. */}
      {spendSource === "live_events" && (
        <div style={{ ...card, borderColor: PT.warn, background: PT.warnS }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: PT.warn }}>
            Showing spend worked out live, because the nightly summary is behind
          </div>
          <div style={{ fontSize: 12, color: PT.txM, marginTop: 4 }}>
            The nightly job that totals usage is switched off, so its figures cover only{" "}
            {fmtNum(rollupCalls)} call{rollupCalls === 1 ? "" : "s"} in this window. The figures below
            are worked out from the calls themselves, at the same rates, so they are current — but
            they are calculated fresh each time this screen loads rather than read from a summary.
          </div>
        </div>
      )}

      {rollupLikelyNotRun && spendSource !== "live_events" && (
        <div style={{ ...card, borderColor: PT.warn, background: PT.warnS }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: PT.warn }}>
            No spend figures for this period
          </div>
          <div style={{ fontSize: 12, color: PT.txM, marginTop: 4 }}>
            There are AI calls recorded in this window but nothing to cost them against. Latency
            below is read from the calls themselves and is unaffected.
          </div>
        </div>
      )}

      {(!focus || focus === "spend") && (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <Stat
              label={`Spend · ${days}d`}
              value={fmtCost(totals?.est_cost)}
              sub={spendSource === "live_events" ? "Worked out live from the calls" : "From the nightly summary"}
            />
            <Stat label="Calls" value={fmtNum(totals?.calls)} sub={`${days}-day window`} />
            <Stat
              label="Error rate"
              value={totals?.calls ? `${(errRate * 100).toFixed(2)}%` : "—"}
              sub={totals ? `${fmtNum(totals.errors)} non-success` : undefined}
              color={totals?.calls ? rateColor(totals.errors, totals.calls) : undefined}
            />
            <Stat label="Grains" value={fmtNum(spend?.length)} sub="tenant × provider × model" />
          </div>

          {/* Spend per provider. The owner asked what each AI supplier costs
              us; the grid below answers a different question (which customer
              used what) and cannot be read for this at a glance. Providers with
              no traffic are listed at zero rather than omitted, so "we spent
              nothing with Groq" is distinguishable from "Groq is missing". */}
          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            <div style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, borderBottom: `1px solid ${PT.brd}` }}>
              What each AI provider cost us · {days}d
              <span style={{ fontWeight: 400, color: PT.txM, marginLeft: 8 }}>
                our cost at the providers' published per-token rates — not what customers are billed
              </span>
            </div>
            {byProvider.length === 0 ? (
              <div style={{ padding: 16, fontSize: 12.5, color: PT.txM }}>No providers configured.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Provider</th>
                    <th style={{ ...th, textAlign: "right" }}>Calls</th>
                    <th style={{ ...th, textAlign: "right" }}>Errors</th>
                    <th style={{ ...th, textAlign: "right" }}>Input tokens</th>
                    <th style={{ ...th, textAlign: "right" }}>Output tokens</th>
                    <th style={{ ...th, textAlign: "right" }}>Models</th>
                    <th style={{ ...th, textAlign: "right" }}>Cost to us</th>
                  </tr>
                </thead>
                <tbody>
                  {byProvider.map((p) => (
                    <tr key={p.provider}>
                      <td style={{ ...td, fontWeight: 600 }}>
                        {p.provider_display_name}
                        {p.in_catalog && p.is_active === false && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: PT.txM, fontWeight: 400 }}>(disabled)</span>
                        )}
                      </td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right" }}>{fmtNum(p.calls)}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: rateColor(p.errors, p.calls) }}>{fmtNum(p.errors)}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtTokens(p.input_tokens)}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtTokens(p.output_tokens)}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtNum(p.models)}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 600 }}>{fmtCost(p.est_cost)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: PT.bg2 }}>
                    <td style={{ ...td, fontWeight: 700 }}>All providers</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 700 }}>{fmtNum(totals?.calls)}</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 700 }}>{fmtNum(totals?.errors)}</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 700 }}>{fmtTokens(totals?.input_tokens)}</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 700 }}>{fmtTokens(totals?.output_tokens)}</td>
                    <td style={{ ...td, textAlign: "right" }} />
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 700 }}>{fmtCost(totals?.est_cost)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            <div style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, borderBottom: `1px solid ${PT.brd}` }}>
              Spend by tenant, provider, and model
            </div>
            {noSpend ? (
              <div style={{ padding: 16, fontSize: 12.5, color: PT.txM }}>
                No rollup rows in this window.
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Tenant</th><th style={th}>Provider</th><th style={th}>Model</th>
                    <th style={{ ...th, textAlign: "right" }}>Calls</th>
                    <th style={{ ...th, textAlign: "right" }}>Errors</th>
                    <th style={{ ...th, textAlign: "right" }}>In</th>
                    <th style={{ ...th, textAlign: "right" }}>Out</th>
                    <th style={{ ...th, textAlign: "right" }}>Est. cost</th>
                  </tr>
                </thead>
                <tbody>
                  {spend!.map((r, i) => (
                    <tr key={i}>
                      {/* The customer's NAME. A NULL tenant_id is the in_house
                          edition or unattributed platform usage — labelled as
                          such, never dropped and never shown as a UUID stub. */}
                      <td style={{ ...td, fontWeight: 600 }}>{tenantLabel(r)}</td>
                      <td style={td}>{providerLabel(r)}</td>
                      <td style={{ ...td, ...PT_NUM, fontSize: 11.5 }}>{r.model}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right" }}>{fmtNum(r.calls)}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: rateColor(Number(r.errors), Number(r.calls)) }}>
                        {fmtNum(r.errors)}
                      </td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtTokens(r.input_tokens)}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtTokens(r.output_tokens)}</td>
                      <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 600 }}>{fmtCost(r.est_cost, r.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* The rates the figures above are computed from, and the only place
              they can be changed. Kept on this screen deliberately: a reader who
              doubts a cost should not have to go looking for the price it came
              from. */}
          {/* What the providers actually invoiced, beside our meter summed
              across every customer. Placed directly above the rate card,
              because the rate card is what OUR figures are computed from and
              the natural next question after seeing the two differ is "what
              rates are we using". */}
          <ProviderBilling days={days} />

          {/* The evidence behind every figure above: each inquiry, the calls it
              made, whether it fell back, and what each one cost. Collapsed by
              default — it is the longest thing on the screen and it is what you
              open when a total looks wrong, not what you come here to read. */}
          <AiRequests days={days} />

          <ModelPrices />
        </>
      )}

      {(!focus || focus === "latency") && (
        <div style={{ ...card, padding: 0, overflowX: "auto" }}>
          <div style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, borderBottom: `1px solid ${PT.brd}` }}>
            Latency and routing outcomes
            <span style={{ fontWeight: 400, fontSize: 11, color: PT.txD, marginLeft: 8 }}>
              percentiles computed from raw events, not averaged from daily rollups
            </span>
          </div>
          {noLatency ? (
            <div style={{ padding: 16, fontSize: 12.5, color: PT.txM }}>No usage events in this window.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Tenant</th>
                  <th style={th}>Provider</th><th style={th}>Model</th>
                  <th style={{ ...th, textAlign: "right" }}>Calls</th>
                  <th style={{ ...th, textAlign: "right" }}>Errors</th>
                  <th style={{ ...th, textAlign: "right" }}>Rerouted</th>
                  <th style={{ ...th, textAlign: "right" }}>Skipped</th>
                  <th style={{ ...th, textAlign: "right" }}>p50</th>
                  <th style={{ ...th, textAlign: "right" }}>p95</th>
                  <th style={{ ...th, textAlign: "right" }}>max</th>
                </tr>
              </thead>
              <tbody>
                {latency!.map((r, i) => (
                  <tr key={i}>
                    {/* Percentiles are per tenant and stay per tenant — there is
                        deliberately no fleet p95 row, because averaging
                        percentiles is not a percentile. */}
                    <td style={{ ...td, fontWeight: 600 }}>{tenantLabel(r)}</td>
                    <td style={td}>{providerLabel(r)}</td>
                    <td style={{ ...td, ...PT_NUM, fontSize: 11.5 }}>{r.model}</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right" }}>{fmtNum(r.calls)}</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", color: rateColor(Number(r.errors), Number(r.calls)) }}>
                      {fmtNum(r.errors)}
                    </td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtNum(r.rerouted)}</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtNum(r.skipped)}</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right" }}>{fmtMs(r.p50_ms)}</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 600 }}>{fmtMs(r.p95_ms)}</td>
                    <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtMs(r.max_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* CONFIGURED BUT NEVER CALLED. A provider that is in the routing catalog
          and has no rows is a fact worth showing: it is the difference between
          "Groq is not set up" and "Groq is set up and nothing ever reached it",
          and only the second one is a routing question. Omitting the row made
          both look identical — which is why Groq appeared to be missing. */}
      {excluded.length > 0 && (
        <div style={{ ...card, borderColor: PT.warn, background: PT.warnS }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: PT.warn }}>
            {fmtNum(excludedCalls)} call{excludedCalls === 1 ? "" : "s"} left out of the figures above
          </div>
          <div style={{ fontSize: 12, color: PT.txM, marginTop: 4 }}>
            These were recorded against a supplier or model the platform does not
            recognise — nothing is configured to route to them. They are almost always
            left over from testing. They are named here rather than quietly dropped.
          </div>
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {excluded.map((e, i) => (
              <span key={i} style={{
                fontFamily: PT.fontMono, fontSize: 11, padding: "2px 8px",
                borderRadius: 999, border: `1px solid ${PT.brd}`, background: PT.bg2, color: PT.txM,
              }}>
                {e.provider} / {e.model} · {fmtNum(e.calls)}
              </span>
            ))}
          </div>
        </div>
      )}

      {silent.length > 0 && (
        <div style={{ ...card }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
            Configured providers with no calls in this window
          </div>
          <div style={{ fontSize: 12, color: PT.txM, marginBottom: 10 }}>
            These are in the routing catalog. Zero calls here means nothing was routed to them in
            the last {days} days — not that they are unconfigured.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {silent.map((p) => (
              <span key={p.provider} style={{
                fontSize: 12, padding: "3px 9px", borderRadius: 7,
                border: `1px solid ${PT.brd}`, background: PT.bg3, color: PT.txM,
              }}>
                {p.provider_display_name} · 0 calls{p.is_active === false ? " · inactive" : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {!unattributedReadable && (
        <div style={{ fontSize: 11.5, color: PT.txD }}>
          Usage recorded against no customer cannot be read on this estate — row-level security
          admits no such row to any session. An empty “Platform / unattributed” line therefore means
          <strong> unreadable</strong>, not zero.
        </div>
      )}
    </div>
  );
};
